import type { ThemeColor } from "@/colors";
import { computed, defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Icon from "../icon/Icon";

export type ResultStatus = "info" | "success" | "warning" | "error" | "404" | "403" | "500" | "418";

const resultStatuses = {
  info: { icon: "lucide:lightbulb", color: "blue" },
  success: { icon: "lucide:party-popper", color: "green" },
  warning: { icon: "lucide:triangle-alert", color: "amber" },
  error: { icon: "lucide:wifi-off", color: "red" },
  "404": { icon: "lucide:ghost", color: "gray" },
  "403": { icon: "lucide:ban", color: "amber" },
  "500": { icon: "lucide:server-crash", color: "red" },
  "418": { icon: "lucide:coffee", color: "amber" },
} satisfies Record<ResultStatus, { icon: string; color: ThemeColor }>;

export default defineComponent({
  name: "Result",
  props: {
    status: p.string<ResultStatus>("error"),
    title: p.string(),
    description: p.string(),
  },
  setup(props, { slots }) {
    const config = computed(() => resultStatuses[props.status] ?? resultStatuses.error);

    return () => (
      <div
        class="flex flex-col items-center justify-center gap-4 p-6 text-center"
        data-accent={config.value.color}
        role="status"
        aria-live="polite"
      >
        <div class="flex size-16 items-center justify-center rounded-full bg-accent-3">
          <Icon icon={config.value.icon} class="size-8 text-accent-9" aria-hidden="true" />
        </div>

        <div class="max-w-sm space-y-1.5">
          {props.title && <h3 class="text-lg font-semibold text-foreground">{props.title}</h3>}
          {props.description && <p class="text-sm text-muted-foreground">{props.description}</p>}
        </div>

        {slots.default && <div class="mt-2">{slots.default()}</div>}
      </div>
    );
  },
});
